import React from 'react';
import _ = require('underscore');

import { InlineAlertType, InlineAlertWidget, TextLinkWidget } from '@tableau/widgets';

import { Filter } from '~commonInfra/utils/Filter';
import { Messages } from '~src/codegen/Localize';
import { ReactActionDialogBodyProps } from '~src/commonInfra/dialogs/ReactActionDialog';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { showAddCredentialAuthModeDialog } from '~src/connectivity/components/AddCredentialAuthModeDialog';
import { EditConnectionDialogBridgeInfo} from '~src/connectivity/components/EditConnection/components/EditConnectionActionDialogBridgeInfo';
import { EditableConnection } from '~src/connectivity/components/EditConnection/IEditConnectionDialog';
import { OAuthCredentials } from '~src/connectivity/resources/OAuthCredentials';
import { IOAuthCredential } from '~src/connectivity/serverApi/types/OAuth/IOAuthCredential';
import { AddOAuthCredentialAction } from '~src/connectivity/services/AddOAuthCredentialAction';
import { AddServiceAccountFileAction } from '~src/connectivity/services/AddServiceAccountFileCredentialAction';
import { ListResult } from '~src/httpClient/fetchers/ListResult';
import { ResourceQuery } from '~src/httpClient/resources/ResourceQuery';
import { SliceableHelper } from '~src/httpClient/utils/SliceableHelper';
import { learnMoreStyle } from '~src/settings/styles/CommonStyles';

import { EditOAuthConnectionDialogProps, EditOAuthConnectionDialogState } from '../../EditOAuthConnectionAction';
import { EditOAuthConnectionDialogEmbeddedToggle } from './EditOAuthConnectionDialogEmbeddedToggle';
import { EditOAuthConnectionDialogPrompt } from './EditOAuthConnectionDialogPrompt';
import { EditOAuthConnectionDialogTestConnection } from './EditOAuthConnectionDialogTestConnection';

export type EditOAuthConnectionDialogBodyProps = ReactActionDialogBodyProps<
    EditOAuthConnectionDialogProps,
    EditOAuthConnectionDialogState
>;

export function EditOAuthConnectionDialogBody(props: EditOAuthConnectionDialogBodyProps) {

  function updateFormState<K extends keyof EditOAuthConnectionDialogState>(
    field: K,
    value: EditOAuthConnectionDialogState[K]
  ) {
    props.updateDialogState({ [field]: value });
  }

    async function OnAddCredentialForSelectedAuthMode(props: any, state: any, updateDialogState: any, type: string){
        if (state.selectedConnectionAuthMode === Messages.addNewSavedCredential_OAuth()) {
            // if this is just a normal OAuth credential we are adding, then call
            // into attemptAuth to start the oauth flow.
            AddOAuthCredentialAction.attemptAuth(type).then(credentialId => {UpdateSelectedCredential(type, credentialId);});
          } else if (state.selectedConnectionAuthMode === Messages.addNewSavedCredential_ServiceAccount()) {
            // otherwise if this is a service account file credential we are adding,
            // try and parse the file and then store the new credential.
            let result = await AddServiceAccountFileAction
            .addServiceAccountFile(state.connAuthState.file, type, updateDialogState);
            UpdateSelectedCredential(type, result.id);
          }

    }

    async function UpdateSelectedCredential(firstConnectionType: string, credentialId: string, oauthConfigId?: string) {
        refreshCredentials(firstConnectionType, oauthConfigId).then((credentialsListResult: ListResult<IOAuthCredential>) => {
            // Set the credentialId if it exists in the list
            if (_.find(credentialsListResult.items, (credential: IOAuthCredential) => credential.id === credentialId)) {
                props.updateDialogState({
                    credentialId: credentialId,
                });
            }
        });
    }

    const refreshCredentials = (
        firstConnectionType: string, oauthConfigId?: string) => {
        const credentialsQuery: ResourceQuery<IOAuthCredential> = oauthConfigId ?
            OAuthCredentials.all().addFilter(Filter.stringFieldEq('oauthConfigId', oauthConfigId)) :
            OAuthCredentials.all().addFilter(Filter.fieldEq('type', firstConnectionType));

        return SliceableHelper.fetchAll(credentialsQuery)
            .then((credentialsListResult: ListResult<IOAuthCredential>) => {
                props.updateDialogState({ credentials: credentialsListResult.items });
                return credentialsListResult; // For chaining
            });
    };

    const handleToggleChange = (isOAuthCredentialOption: boolean) => {
        if (isOAuthCredentialOption) {
            props.updateDialogState({ credentialOption: 'oauth' });
        } else {
            props.updateDialogState({ credentialOption: 'none' });
        }
    };

    const handleUsingRemoteQueryAgentChange = (isUsingRemoteQueryAgent: boolean) => {
      props.updateDialogState({ usingRemoteQueryAgent: isUsingRemoteQueryAgent});
    };

    const handleCredentialIdChange = (credentialId: string) => {
        if (credentialId === props.addNewOAuthCredentialOptionValue) {
            props.updateDialogState({
                credentialId: undefined,
            });
            const firstConnection: EditableConnection = props.firstConnection;
            const firstConnectionType = firstConnection.connectionType;
            const firstConnectionDetails = firstConnection.connectionDetails;
            let instanceUrl: string;
            let role: string;
            let oauthConfigId: string;
            let server: string;
            if (firstConnectionDetails.type === 'database') {
                instanceUrl = firstConnectionDetails.instanceUrl;
                role = firstConnectionDetails.role;
                oauthConfigId = firstConnectionDetails.oauthConfigId;
                server = firstConnectionDetails.serverName;
            }

            if (firstConnectionType === 'bigquery' || firstConnectionType === 'googlebigquery') {
                // TFS1243460 For GBQ, we now support Service Account file credentials AND OAuth, so the user must pick the mode.
                showAddCredentialAuthModeDialog(firstConnectionType, OnAddCredentialForSelectedAuthMode);
            }
            else{
                AddOAuthCredentialAction.attemptAuth(firstConnectionType, instanceUrl, role, oauthConfigId, server).then((credentialId: string) =>{
                    UpdateSelectedCredential(firstConnectionType, credentialId, oauthConfigId);
                });
            }
        } else {
            props.updateDialogState({ credentialId: credentialId });
        }
    };

    const learnMoreLink = (
      <TextLinkWidget
        className={learnMoreStyle}
        href={HelpService.getServerHelpUrlByCategory('managed-keychain')}
        target='_blank'
        testId={'learn-more'}
      >
        {Messages.learnMore()}
      </TextLinkWidget>
    );

    return (
        <>
            <EditOAuthConnectionDialogPrompt prompt={props.prompt} />
            <div style={{ width: '100%' }}>
                <EditOAuthConnectionDialogEmbeddedToggle
                    credentialNoValueOptionText={props.dialogState.credentialNoValueOptionText}
                    currentConnectionTypeDisplayName={props.currentConnectionTypeDisplayName}
                    addNewOAuthCredentialOptionValue={props.addNewOAuthCredentialOptionValue}
                    credentialId={props.dialogState.credentialId}
                    credentials={props.dialogState.credentials}
                    credentialOption={props.dialogState.credentialOption}
                    handleToggleChange={handleToggleChange}
                    handleCredentialIdChange={handleCredentialIdChange}
                />

                {props.dialogState.usingManagedKeychain && <InlineAlertWidget
                  testId={'managed-keychain-alert'}
                  type={InlineAlertType.Warning}
                  children={<span>
                    {Messages.managedKeychainEditConnectionWarningMessage()}
                    {learnMoreLink}
                  </span>}/>}

                <EditConnectionDialogBridgeInfo
                  usingRemoteQueryAgent={props.dialogState.usingRemoteQueryAgent}
                  handleUsingRemoteQueryAgentChange={handleUsingRemoteQueryAgentChange}
                  showNetworkType={props.showNetworkType}
                />

                <EditOAuthConnectionDialogTestConnection
                    dialogState={props.dialogState}
                    onTestConnection={props.testConnection}
                    isTestConnectionDisabled={props.isTestConnectionDisabled(props.dialogState)}
                />
            </div>
        </>
    );
}
